import { useEffect, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { siteInfo, reviews } from "@/mocks/manga";

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "关于我们 - 九妖漫画网 | 免费漫画阅读平台";
  }, []);

  const breadcrumbItems = [
    { name: "首页", path: "/" },
    { name: "关于我们" },
  ];

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: "https://www.jiuyaomh.com",
    description: siteInfo.description,
    foundingDate: String(siteInfo.year),
    contactPoint: {
      "@type": "ContactPoint",
      email: siteInfo.email,
      contactType: "customer service",
      availableLanguage: ["Chinese"],
    },
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `关于${siteInfo.name}`,
    description: `${siteInfo.name}品牌介绍 - ${siteInfo.description}`,
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch("https://readdy.ai/api/form/d9oov60senbniolesj70", {
        method: "POST",
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const responseText = await response.text();
      if (response.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError("提交失败，请稍后再试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络后重试");
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          {/* Page Header */}
          <section className="relative w-full h-[250px] md:h-[320px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20library%20or%20reading%20room%20filled%20with%20manga%20and%20comic%20books%20on%20wooden%20shelves%2C%20soft%20warm%20lighting%20from%20a%20lamp%2C%20comfortable%20atmosphere%2C%20stylized%20illustration%2C%20clean%20composition&width=1600&height=400&seq=about-hero-bg&orientation=landscape"
              alt="关于九妖漫画网"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-4 text-center">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                关于九妖漫画网
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl">
                专注免费漫画阅读服务，为漫画爱好者打造极致的下拉式阅读体验
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                <div className="flex-1">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      <strong>{siteInfo.name}</strong>成立于{siteInfo.year}年，是国内领先的免费漫画在线阅读平台。我们致力于为广大漫画爱好者提供最优质的免费漫画资源，涵盖<strong>国漫推荐</strong>、<strong>日漫精选</strong>、<strong>韩漫热门</strong>等多个分类。
                    </p>
                    <p>
                      平台采用先进的<strong>免费下拉式追更</strong>技术，让读者可以流畅地不间断阅读漫画，无需频繁翻页，大大提升了阅读体验。无论是热血战斗、甜蜜恋爱还是悬疑推理，在这里都能找到你喜欢的作品。
                    </p>
                    <p>
                      经过多年的发展，九妖漫画网已经积累了超过<strong>15,000部漫画资源</strong>，日活跃用户超过<strong>500万</strong>。我们始终坚持免费服务的原则，让每一位漫画爱好者都能无门槛地享受漫画阅读的乐趣。
                    </p>
                    <p>
                      我们的团队由一群热爱漫画的技术人和内容编辑组成，每天都在为提供更好的阅读体验而努力。从内容筛选到技术优化，从用户体验到社区建设，我们用心做好每一个环节。
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 w-64 md:w-72">
                  <div className="rounded-lg overflow-hidden bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20stylized%20illustration%20of%20a%20cozy%20manga%20reading%20space%20with%20a%20person%20reading%20comics%20on%20a%20tablet%20surrounded%20by%20floating%20manga%20panels%2C%20warm%20ambient%20lighting%2C%20anime-inspired%20art%2C%20clean%20composition&width=400&height=500&seq=about-brand-img&orientation=portrait"
                      alt="九妖漫画网品牌形象"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Reviews */}
          <section className="bg-background-100 border-y border-background-200/70">
            <div className="px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    用户口碑
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">听听漫画爱好者对我们的真实评价</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                          <img src={review.avatar} alt={review.user} className="w-full h-full object-cover" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground-800">{review.user}</p>
                          <div className="flex gap-0.5 mt-0.5">
                            {Array.from({ length: review.rating }).map((_, i) => (
                              <i key={i} className="ri-star-fill text-xs text-accent-500" />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                      <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  发展历程
                </h2>
                <p className="text-sm text-foreground-500 mt-2">九妖漫画网的成长之路</p>
              </div>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px" />
                <div className="space-y-8">
                  {[
                    { year: "2018", title: "平台创立", desc: "九妖漫画网正式上线，以免费漫画阅读为核心服务，首批上线500部漫画资源。" },
                    { year: "2019", title: "快速增长", desc: "漫画资源突破3000部，日活跃用户突破100万，成为漫画阅读领域的新兴力量。" },
                    { year: "2020", title: "技术创新", desc: "推出免费下拉式追更功能，优化阅读体验，用户留存率大幅提升。" },
                    { year: "2022", title: "内容升级", desc: "漫画资源突破10000部，新增韩漫热门专区，覆盖更多用户群体。" },
                    { year: "2024", title: "品质飞跃", desc: "日活跃用户突破500万，全面升级阅读界面和推荐算法，成为行业领先平台。" },
                    { year: "2026", title: "持续进化", desc: "持续优化用户体验，丰富内容库，致力于打造最好的免费漫画阅读平台。" },
                  ].map((item, i) => (
                    <div key={item.year} className={`relative flex items-start gap-6 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 bg-primary-500 rounded-full -translate-x-1.5 mt-1.5 z-10" />
                      <div className={`ml-10 md:ml-0 md:w-1/2 ${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                        <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
                          <span className="text-xs font-bold text-primary-500">{item.year}</span>
                          <h3 className="text-base font-heading font-semibold text-foreground-900 mt-1">{item.title}</h3>
                          <p className="text-sm text-foreground-600 mt-2 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                      <div className="hidden md:block md:w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="bg-background-100 border-t border-background-200/70">
            <div className="px-4 md:px-6 py-10 md:py-14">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500 mt-2">有任何问题或建议，欢迎随时联系我们</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-background-50 rounded-lg p-4 text-center border border-background-200/70">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-mail-line text-lg" />
                    </div>
                    <p className="text-xs text-foreground-500">电子邮箱</p>
                    <p className="text-sm font-medium text-foreground-800 mt-1">{siteInfo.email}</p>
                  </div>
                  <div className="bg-background-50 rounded-lg p-4 text-center border border-background-200/70">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-lg" />
                    </div>
                    <p className="text-xs text-foreground-500">微信公众号</p>
                    <p className="text-sm font-medium text-foreground-800 mt-1">{siteInfo.wechat}</p>
                  </div>
                  <div className="bg-background-50 rounded-lg p-4 text-center border border-background-200/70">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-qq-line text-lg" />
                    </div>
                    <p className="text-xs text-foreground-500">QQ交流群</p>
                    <p className="text-sm font-medium text-foreground-800 mt-1">{siteInfo.qq}</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-6 border border-background-200/70" data-readdy-form="">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    />
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <div className="spam-guard">
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>

                  {formStatus === "success" && (
                    <div className="mb-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm">
                      感谢您的留言！我们会尽快回复您。
                    </div>
                  )}
                  {formStatus === "error" && (
                    <div className="mb-4 p-3 rounded-lg bg-primary-100 text-primary-700 text-sm">
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-white rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    发送留言
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}